/**
 * DemocratCharts — trading state.
 *
 * The currently-open trade and the user's chosen risk parameters.
 * Closed trades live in the backend (queried via useTrades); this store
 * only tracks what's currently active in the UI.
 *
 * Account balance & risk % persist to localStorage so they survive reloads.
 */

import { create } from "zustand";

const LS_PREFS = "livecharts:trading-prefs";

interface TradingPrefs {
  accountBalance: number;
  riskPercent: number;
  defaultQuantity: number;
}

function loadPrefs(): TradingPrefs {
  try {
    const raw = localStorage.getItem(LS_PREFS);
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return DEFAULT_PREFS;
}

function savePrefs(p: TradingPrefs) {
  // Callers pass `{...get(), field: v}` — the whole store. Pick only the pref
  // fields so we never persist the open-trade object (or anything future) and
  // accidentally resurrect stale state on reload.
  const clean: TradingPrefs = {
    accountBalance: p.accountBalance,
    riskPercent: p.riskPercent,
    defaultQuantity: p.defaultQuantity,
  };
  try {
    localStorage.setItem(LS_PREFS, JSON.stringify(clean));
  } catch {
    /* ignore */
  }
}

const DEFAULT_PREFS: TradingPrefs = {
  accountBalance: 100_000,
  riskPercent: 1,
  defaultQuantity: 10,
};

export interface OpenTrade {
  id: string;                       // server-assigned UUID
  symbol: string;
  exchange: string;
  timeframe: string;
  direction: "long" | "short";
  quantity: number;
  entryTs: string;
  entryPrice: number;
  slPrice: number | null;
  tpPrice: number | null;
}

interface TradingState extends TradingPrefs {
  open: OpenTrade | null;

  setAccountBalance: (v: number) => void;
  setRiskPercent: (v: number) => void;
  setDefaultQuantity: (v: number) => void;

  setOpen: (t: OpenTrade | null) => void;
  updateOpenSL: (price: number | null) => void;
  updateOpenTP: (price: number | null) => void;
}

export const useTradingStore = create<TradingState>((set, get) => {
  const initial = loadPrefs();
  return {
    ...initial,
    open: null,

    setAccountBalance: (v) => {
      set({ accountBalance: v });
      savePrefs({ ...get(), accountBalance: v });
    },
    setRiskPercent: (v) => {
      set({ riskPercent: v });
      savePrefs({ ...get(), riskPercent: v });
    },
    setDefaultQuantity: (v) => {
      set({ defaultQuantity: v });
      savePrefs({ ...get(), defaultQuantity: v });
    },

    setOpen: (t) => set({ open: t }),
    updateOpenSL: (price) => {
      const o = get().open;
      if (o) set({ open: { ...o, slPrice: price } });
    },
    updateOpenTP: (price) => {
      const o = get().open;
      if (o) set({ open: { ...o, tpPrice: price } });
    },
  };
});
